import {
	Button,
	ButtonTypes,
	Checkbox,
	Collapsible,
	CollapsibleTrigger,
	CollapsibleContent,
	TextBox,
	DropDownList,
} from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useState } from "react";

import {
	MyViewController as Api,
	type Catalog,
	type MyViewWidget,
	type MyViewBoard,
	type WidgetFilter,
} from "../../../api/myViewController";
import { Dialog } from "../shared/dialog";
import commonStyles from "../shared/style.module.scss";
import styles from "./style.module.scss";
import { configuration, filterFields, getValue, initialFilter } from "./widgetConfiguration";

export function WidgetSettings({
	widget,
	boardId,
	boards,
	catalog,
	onClose,
	onSaved,
}: {
	widget?: MyViewWidget;
	boardId: number;
	boards: MyViewBoard[];
	catalog: Catalog;
	onClose: () => void;
	onSaved: (targetBoardId: number) => Promise<void>;
}) {
	const [name, setName] = useState(widget?.widgetName ?? "");
	const [targetBoardId, setTargetBoardId] = useState(boardId);
	const [title, setTitle] = useState(widget?.title ?? "");
	const [timeframe, setTimeframe] = useState(widget?.timeframe ?? 2);
	const [filter, setFilter] = useState<WidgetFilter>(() => initialFilter(widget));
	const [custom, setCustom] = useState<Record<string, unknown>>(() => configuration(widget));
	const [search, setSearch] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const definition = catalog.definitions.find((d) => d.widgetName === name);
	const filters = useQuery({
		queryKey: [
			"myViewV2",
			"filters",
			filter.brandIds,
			filter.channelIds,
			filter.marketerIds,
			filter.usStateIds,
			filter.branchIds,
		],
		queryFn: ({ signal }) => Api.getFilters(filter, signal),
		staleTime: 60_000,
	});
	const dispositions = useQuery({
		queryKey: ["myViewV2", "dispositions", filter],
		queryFn: ({ signal }) => Api.getDispositions(filter, signal),
		enabled: name === "LeadActivities",
		staleTime: 60_000,
	});
	const useDispositionTypes = getValue(custom, "UseDispositionTypes") ?? true;
	const definitions = catalog.definitions.filter((d) =>
		`${d.title} ${d.group}`.toLowerCase().includes(search.toLowerCase()),
	);
	function changeFilter(key: keyof WidgetFilter, values: number[]) {
		const index = filterFields.findIndex((f) => f.key === key);
		setFilter((previous) => {
			const next = { ...previous, [key]: values };
			for (const field of filterFields.slice(index + 1)) next[field.key] = undefined;
			return next;
		});
	}
	async function save(e: React.FormEvent) {
		e.preventDefault();
		if (!definition) return;
		setError("");
		if (timeframe === 7 && (!filter.startDate || (filter.endDate && filter.endDate < filter.startDate))) {
			setError("Choose a start date and an end date on or after it.");
			return;
		}
		if (
			timeframe === 7 &&
			!definition.noThreeMonthLimit &&
			filter.startDate &&
			(new Date(filter.endDate || new Date().toLocaleDateString("en-CA")).getTime() -
				new Date(filter.startDate).getTime()) /
				86400000 >
				90
		) {
			setError("This widget supports custom date ranges of up to 90 days.");
			return;
		}
		setBusy(true);
		try {
			const parameters = Object.fromEntries(
				(definition.customParameters ?? []).map((p) => [p.name, getValue(custom, p.name) ?? p.defaultValue]),
			);
			if (name === "LeadActivities" && useDispositionTypes) {
				parameters.DisplayAsLeaderboard = false;
				parameters.CustomDispositions = ["0"];
			}
			await Api.saveWidget({
				id: widget?.id ?? 0,
				boardId: targetBoardId,
				widgetName: name,
				title,
				timeframe,
				filter,
				customFieldsJson: JSON.stringify(parameters),
			});
			await onSaved(targetBoardId);
			onClose();
		} catch {
			setError("Unable to save this widget. Check the settings and try again.");
		} finally {
			setBusy(false);
		}
	}
	return (
		<Dialog title={widget ? "Widget settings" : "Add a widget"} onClose={onClose} busy={busy}>
			<form onSubmit={save}>
				<div className={commonStyles.dialogBody}>
					{!widget && (
						<>
							<p className={commonStyles.muted}>Choose a report, then make it yours.</p>
							<TextBox
								id="mv-widget-search"
								label={<span className={styles.screenReaderOnly}>Find a widget</span>}
								leftAddon={<Search size={17} />}
								placeholder="Find a widget…"

								value={search}
								changeHandler={setSearch}
							/>
							<div className={styles.catalog} role="group" aria-label="Widget types">
								{definitions.map((d) => (
									<Button
										type="button"
										key={d.widgetName}
										types={[d.widgetName === name ? ButtonTypes.Primary : ButtonTypes.Outline]}
										aria-pressed={d.widgetName === name}
										onClick={() => {
											setName(d.widgetName);
											setTitle(d.title);
											setCustom({});
											setFilter((previous) => ({
												...previous,
												channelIds: d.limitToChannelIds ?? [],
												marketerIds: [],
												usStateIds: [],
												branchIds: [],
											}));
										}}
									>
										<span>{d.title}</span>
										<small>{d.group}</small>
									</Button>
								))}
								{!definitions.length && <p>No matching widgets.</p>}
							</div>
						</>
					)}
					{definition && (
						<>
							<TextBox
								id="mv-title"
								label="Title"
								required
								maxlength={200}
								value={title}
								changeHandler={setTitle}
							/>
							<DropDownList
								fullLength
								id="mv-target-board"
								label="Board"
								value={targetBoardId}
								values={boards}
								valueProp="id"
								textProp="title"
								// Ignores auto-selection so only a user's pick changes the board.
								changeHandler={(value, _id, automatic) => {
									if (!automatic && value != null) setTargetBoardId(value);
								}}
							/>
							<DropDownList
								fullLength
								id="mv-timeframe"
								label="Timeframe"
								value={timeframe}
								values={
									catalog.timeframes.some((t) => t.value === timeframe)
										? catalog.timeframes
										: [...catalog.timeframes, { value: timeframe, name: "Current timeframe" }]
								}
								valueProp="value"
								textProp="name"
								// Ignores auto-selection so only a user's pick changes the timeframe.
								changeHandler={(value, _id, automatic) => {
									if (!automatic && value != null) setTimeframe(value);
								}}
							/>
							{timeframe === 7 && (
								<div className={styles.fieldGrid}>
									<TextBox
										id="mv-from"
										label="From"
										type="date"
										required
										value={filter.startDate ?? ""}
										// The mv- id keeps the DOM id unique, so it can't double as the filter key.
										changeHandler={(value) => setFilter({ ...filter, startDate: value })}
									/>
									<TextBox
										id="mv-through"
										label="Through"
										type="date"
										inputMin={filter.startDate}
										value={filter.endDate ?? ""}
										// The mv- id keeps the DOM id unique, so it can't double as the filter key.
										changeHandler={(value) => setFilter({ ...filter, endDate: value || undefined })}
									/>
								</div>
							)}
							<Collapsible className={styles.filterSection}>
								<CollapsibleTrigger asChild>
									<Button types={[ButtonTypes.Text]}>Filter this report</Button>
								</CollapsibleTrigger>
								<CollapsibleContent>
									<p className={commonStyles.muted}>
										Leave a filter empty to include all accessible values.
									</p>
									{filters.isError && (
										<p role="alert">
											Filters could not load.{" "}
											<Button type="button" onClick={() => filters.refetch()}>
												Retry
											</Button>
										</p>
									)}
									<div className={styles.fieldGrid}>
										{filterFields
											.filter((f) => f.key !== "utilityIds" || definition.supportsUtilityFilter)
											.map((f) => (
												<DropDownList
													fullLength
													key={f.key}
													id={`mv-filter-${f.key}`}
													label={f.title}
													isMulti
													clearable
													value={(filter[f.key] as number[]) ?? []}
													disabled={filters.isFetching}
													loaded={!filters.isFetching}
													valueProp="id"
													textProp="name"
													values={(filters.data?.[f.options] ?? []).filter(
														(o) =>
															o.id > 0 &&
															(f.key !== "channelIds" ||
																!definition.limitToChannelIds?.length ||
																definition.limitToChannelIds.includes(o.id)),
													)}
													// Ignores auto-selection; the mv- id can't double as the filter key.
													changeHandler={(values, _id, automatic) => {
														if (!automatic) changeFilter(f.key, values ?? []);
													}}
												/>
											))}
									</div>
								</CollapsibleContent>
							</Collapsible>
							{definition.customParameters
								?.filter((p) => p.dataType !== 4)
								.map((p) => {
									const value = getValue(custom, p.name) ?? p.defaultValue;
									return (
										<div key={p.name}>
											{p.dataType === 0 ? (
												<Checkbox
													id={`mv-custom-${p.name}`}
													label={p.prompt || p.name}
													disabled={
														p.readOnly ||
														(name === "LeadActivities" &&
															p.name === "DisplayAsLeaderboard" &&
															!!useDispositionTypes)
													}
													value={
														name === "LeadActivities" &&
														p.name === "DisplayAsLeaderboard" &&
														useDispositionTypes
															? false
															: value === true || value === "true"
													}
													// The mv-custom- id can't double as the parameter name.
													changeHandler={(nextValue) =>
														setCustom({ ...custom, [p.name]: nextValue })
													}
												/>
											) : (
												<TextBox
													id={`mv-custom-${p.name}`}
													label={p.prompt || p.name}
													required
													disabled={p.readOnly}
													type={
														p.dataType === 2 ? "number" : p.dataType === 3 ? "time" : "text"
													}
													step={p.dataType === 2 ? "any" : undefined}
													value={String(value ?? "")}
													// The mv-custom- id can't double as the parameter name, and numbers need converting.
													changeHandler={(nextValue) =>
														setCustom({
															...custom,
															[p.name]: p.dataType === 2 ? Number(nextValue) : nextValue,
														})
													}
												/>
											)}
										</div>
									);
								})}
							{name === "LeadActivities" && !useDispositionTypes && (
								<div>
									<DropDownList
										fullLength
										id="mv-dispositions"
										label="Dispositions"
										isMulti
										clearable
										disabled={dispositions.isFetching}
										loaded={!dispositions.isFetching}
										valueProp="id"
										textProp="name"
										values={(dispositions.data ?? []).map((d) => ({ ...d, id: String(d.id) }))}
										value={((getValue(custom, "CustomDispositions") as string[]) ?? ["0"]).map(
											String,
										)}
										// Ignores auto-selection so only a user's pick changes the dispositions.
										changeHandler={(values, _id, automatic) => {
											if (!automatic) setCustom({ ...custom, CustomDispositions: values ?? [] });
										}}
									/>
									{dispositions.isError && (
										<span role="alert">
											Unable to load dispositions.{" "}
											<Button type="button" onClick={() => dispositions.refetch()}>
												Retry
											</Button>
										</span>
									)}
								</div>
							)}
						</>
					)}
					{error && (
						<p className={commonStyles.error} role="alert">
							{error}
						</p>
					)}
				</div>
				<footer>
					<Button type="button" disabled={busy} onClick={onClose}>
						Cancel
					</Button>
					<Button types={[ButtonTypes.Primary]} disabled={busy || !definition} type="submit">
						{busy ? "Saving…" : widget ? "Save changes" : "Add widget"}
					</Button>
				</footer>
			</form>
		</Dialog>
	);
}
